import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import '../styles/Submissions.css';

const SortIcon = ({ direction }) => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2">
    {direction === 'asc' ? (
      <>
        <polyline points="1 4 7 1 13 4"></polyline>
        <polyline points="1 10 7 7 13 10"></polyline>
      </>
    ) : direction === 'desc' ? (
      <>
        <polyline points="1 10 7 13 13 10"></polyline>
        <polyline points="1 4 7 7 13 4"></polyline>
      </>
    ) : (
      <>
        <polyline points="1 4 7 1 13 4"></polyline>
        <polyline points="1 10 7 13 13 10"></polyline>
      </>
    )}
  </svg>
);

const ViewIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
    <circle cx="12" cy="12" r="3"></circle>
  </svg>
);

const ApproveIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20 6 9 17 4 12"></polyline>
  </svg>
);

const RejectIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

export default function PrincipalSubmissions({ apiBase }) {
  const toastUtil = useToast();
  const toast = toastUtil;

  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'submitted_at', direction: 'desc' });

  useEffect(() => {
    loadSubmissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadSubmissions = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${apiBase}/submissions/pending?limit=100&t=${Date.now()}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) throw new Error(`API returned ${response.status}`);

      const result = await response.json();
      if ((result.success || result.ok) && Array.isArray(result.data)) {
        setSubmissions(result.data);
      } else {
        setSubmissions([]);
      }
    } catch (err) {
      toast.error('Error loading submissions: ' + err.message);
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedSubmissions = () => {
    const sorted = [...submissions].sort((a, b) => {
      const key = sortConfig.key;
      let aVal = a[key];
      let bVal = b[key];

      if (key === 'submitted_at') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  };

  const handleApprove = async () => {
    if (!selectedSubmission) return;
    setApproving(true);
    try {
      const response = await fetch(`${apiBase}/submissions/${selectedSubmission.id}/approve`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      const result = await response.json();
      if (result.success || result.ok) {
        toast.success('Approved! Pages updating...');
        
        // SIGNAL: Dispatch custom event to trigger AboutUsPage, HomePage, and any other page to refresh
        window.dispatchEvent(new CustomEvent('approvalCompleted', {
          detail: {
            change_id: selectedSubmission.id,
            section_type: selectedSubmission.section_type,
            timestamp: Date.now()
          }
        }));
        
        // Also set localStorage flag for pages that use storage events
        localStorage.setItem('approvalTriggered', 'true');
        
        // Reload page to ensure all listeners trigger
        setSubmissions(prev => prev.filter(s => s.id !== selectedSubmission.id));
        setSelectedSubmission(null);
        setTimeout(() => window.location.reload(), 1500);
      } else {
        toast.error(result.error || result.message || 'Approval failed');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!selectedSubmission || !rejectReason.trim()) {
      toast.error('Reason is required');
      return;
    }

    setRejecting(true);
    try {
      const response = await fetch(`${apiBase}/submissions/${selectedSubmission.id}/reject`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rejection_reason: rejectReason })
      });

      const result = await response.json();
      if (result.success || result.ok) {
        toast.success('Rejected! Admin notified');
        setSubmissions(prev => prev.filter(s => s.id !== selectedSubmission.id));
        setSelectedSubmission(null);
        setRejectReason('');
      } else {
        toast.error(result.error || result.message || 'Rejection failed');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setRejecting(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMenuLabel = (sectionType) => {
    const menus = {
      'hero': 'Hero Section',
      'quicklinks': 'Quick Links',
      'highlights': 'Highlights',
      'news': 'News',
      'countdown': 'Countdown',
      'education-program': 'Education Program',
      'secondary': 'Secondary',
      'about-us': 'About Us',
      'admissions': 'Admissions',
      'footer': 'Footer'
    };
    return menus[sectionType] || sectionType;
  };

  const getMenuPage = (sectionType) => {
    const pages = {
      'hero': 'Homepage',
      'quicklinks': 'Homepage',
      'highlights': 'Homepage',
      'news': 'Homepage',
      'countdown': 'Homepage',
      'education-program': 'Education Program',
      'secondary': 'Secondary',
      'about-us': 'About Us',
      'admissions': 'Admissions',
      'footer': 'Homepage'
    };
    return pages[sectionType] || 'Homepage';
  };

  const tryParseJSON = (str) => {
    try {
      return JSON.parse(str);
    } catch {
      return str;
    }
  };

  const isJsonValue = (val) => {
    try {
      const parsed = JSON.parse(val);
      return typeof parsed === 'object' && parsed !== null;
    } catch {
      return false;
    }
  };

  const formatValue = (val, fieldId = '') => {
    const parsed = tryParseJSON(val);
    
    // Handle card objects (cards.0, cards.1, cards.2)
    if (typeof parsed === 'object' && parsed !== null && (fieldId.startsWith('cards.') || (parsed.id && parsed.title !== undefined && parsed.text !== undefined))) {
      return `${parsed.title || ''}\n\n${parsed.text || ''}`;
    }
    
    // Handle item operations (items.0.add, items.1.update, items.2.delete)
    if (typeof parsed === 'object' && parsed !== null && (fieldId.includes('items.') && (fieldId.includes('.add') || fieldId.includes('.update') || fieldId.includes('.delete')))) {
      // Format highlight items - show title, summary
      if (parsed.title !== undefined && parsed.summary !== undefined) {
        return `${parsed.title || '(No title)'}\n\n${parsed.summary || '(No description)'}`;
      }
    }
    
    // Handle other objects - show as readable format
    if (typeof parsed === 'object' && parsed !== null) {
      return JSON.stringify(parsed, null, 2);
    }
    
    return String(parsed);
  };

  if (loading) {
    return (
      <div className="submissions-page">
        <div className="page-header">
          <div>
            <h1>Submissions</h1>
            <p>Review pending content submissions</p>
          </div>
        </div>
        <div className="loading">Loading submissions...</div>
      </div>
    );
  }

  if (submissions.length === 0) {
    return (
      <div className="submissions-page">
        <div className="page-header">
          <div>
            <h1>Submissions</h1>
            <p>Review pending content submissions</p>
          </div>
        </div>
        <div className="empty-state">
          <div className="empty-icon">Done</div>
          <h3>All caught up!</h3>
          <p>No pending submissions to review</p>
        </div>
      </div>
    );
  }

  const sortedSubmissions = getSortedSubmissions();

  return (
    <div className="submissions-page">
      <div className="page-header">
        <div>
          <h1>Submissions</h1>
          <p>Review pending content submissions</p>
        </div>
        <span className="count-badge">{submissions.length} pending</span>
      </div>

      <div className="submissions-table-wrapper">
        <table className="submissions-table">
          <thead>
            <tr>
              <th>
                <button className="sort-header" onClick={() => handleSort('id')}>
                  No
                  <SortIcon direction={sortConfig.key === 'id' ? sortConfig.direction : null} />
                </button>
              </th>
              <th>
                <button className="sort-header" onClick={() => handleSort('page')}>
                  Menu
                  <SortIcon direction={sortConfig.key === 'page' ? sortConfig.direction : null} />
                </button>
              </th>
              <th>
                <button className="sort-header" onClick={() => handleSort('section_type')}>
                  Section
                  <SortIcon direction={sortConfig.key === 'section_type' ? sortConfig.direction : null} />
                </button>
              </th>
              <th>
                <button className="sort-header" onClick={() => handleSort('submitted_by')}>
                  Submitted By
                  <SortIcon direction={sortConfig.key === 'submitted_by' ? sortConfig.direction : null} />
                </button>
              </th>
              <th>
                <button className="sort-header" onClick={() => handleSort('submitted_at')}>
                  Date
                  <SortIcon direction={sortConfig.key === 'submitted_at' ? sortConfig.direction : null} />
                </button>
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedSubmissions.map((sub, index) => (
              <tr key={sub.id} className="submission-row">
                <td className="no-cell">{index + 1}</td>
                <td className="menu-cell">
                  <span className="menu-badge">{getMenuPage(sub.section_type)}</span>
                </td>
                <td className="section-cell">
                  <span className="section-label">{getMenuLabel(sub.section_type)}</span>
                </td>
                <td className="submitter-cell">
                  <span className="submitter-name">{sub.submitted_by_name || 'Unknown'}</span>
                </td>
                <td className="date-cell">
                  <span className="date-text">{formatDate(sub.submitted_at)}</span>
                </td>
                <td className="actions-cell">
                  <button
                    className="btn-view"
                    onClick={() => {
                      setSelectedSubmission(sub);
                      setRejectReason('');
                    }}
                    title="View submission details"
                  >
                    <ViewIcon /> View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedSubmission && (
        <div className="modal-overlay" onClick={() => setSelectedSubmission(null)}>
          <div className="detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Submission Details</h2>
              <button className="btn-close" onClick={() => setSelectedSubmission(null)}>
                X
              </button>
            </div>

            <div className="modal-body">
              <div className="detail-section">
                <h3 className="section-title">Information</h3>
                <div className="info-grid">
                  <div className="info-row">
                    <label>Menu</label>
                    <span className="info-value">{getMenuPage(selectedSubmission.section_type)}</span>
                  </div>
                  <div className="info-row">
                    <label>Section</label>
                    <span className="info-value">{getMenuLabel(selectedSubmission.section_type)}</span>
                  </div>
                  <div className="info-row">
                    <label>Field</label>
                    <span className="info-value">{selectedSubmission.field_name || selectedSubmission.field_id}</span>
                  </div>
                  <div className="info-row">
                    <label>Submitted By</label>
                    <span className="info-value">{selectedSubmission.submitted_by_name || 'Unknown'}</span>
                  </div>
                  <div className="info-row full-width">
                    <label>Date</label>
                    <span className="info-value">{formatDate(selectedSubmission.submitted_at)}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3 className="section-title">Content Changes</h3>
                <div className="changes-container">
                  <div className="change-item">
                    <div className="change-header before">Before</div>
                    <div className={`change-value before-value ${isJsonValue(selectedSubmission.old_value) ? 'json' : ''}`}>
                      {formatValue(selectedSubmission.old_value, selectedSubmission.field_id) || '(empty)'}
                    </div>
                  </div>
                  <div className="change-arrow">to</div>
                  <div className="change-item">
                    <div className="change-header after">After</div>
                    <div className={`change-value after-value ${isJsonValue(selectedSubmission.new_value) ? 'json' : ''}`}>
                      {formatValue(selectedSubmission.new_value, selectedSubmission.field_id)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3 className="section-title">Live Preview</h3>
                <div className="preview-container">
                  <div className="preview-item">
                    <div className="preview-label">Current (Before Approval)</div>
                    <div className="preview-content">
                      <div className="preview-box">
                        <h4>Current Website Display</h4>
                        <div className="hero-preview current">
                          {selectedSubmission.field_id === 'tagline_prefix' && (
                            <div className="tagline">
                              <span className="prefix-label">{tryParseJSON(selectedSubmission.old_value) || 'SCHOOL FOR'}</span>
                            </div>
                          )}
                          {selectedSubmission.field_id === 'tagline_accent' && (
                            <div className="tagline">
                              <span className="accent-label">{tryParseJSON(selectedSubmission.old_value) || 'LIFE'}</span>
                            </div>
                          )}
                          {selectedSubmission.field_id === 'philosophy' && (
                            <div className="philosophy-preview">
                              {tryParseJSON(selectedSubmission.old_value) || 'School philosophy...'}
                            </div>
                          )}
                          {selectedSubmission.field_id === 'video_file' && (
                            <div className="video-preview">
                              Video: {tryParseJSON(selectedSubmission.old_value) || 'No video'}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="preview-arrow">After Approval</div>

                  <div className="preview-item">
                    <div className="preview-label">Updated (After Approval)</div>
                    <div className="preview-content">
                      <div className="preview-box">
                        <h4>Updated Website Display</h4>
                        <div className="hero-preview updated">
                          {selectedSubmission.field_id === 'tagline_prefix' && (
                            <div className="tagline">
                              <span className="prefix-label">{tryParseJSON(selectedSubmission.new_value)}</span>
                            </div>
                          )}
                          {selectedSubmission.field_id === 'tagline_accent' && (
                            <div className="tagline">
                              <span className="accent-label">{tryParseJSON(selectedSubmission.new_value)}</span>
                            </div>
                          )}
                          {selectedSubmission.field_id === 'philosophy' && (
                            <div className="philosophy-preview">
                              {tryParseJSON(selectedSubmission.new_value)}
                            </div>
                          )}
                          {selectedSubmission.field_id === 'video_file' && (
                            <div className="video-preview">
                              Video: {tryParseJSON(selectedSubmission.new_value)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3 className="section-title">Decision</h3>
                {rejecting === false && (
                  <div className="action-buttons">
                    <button className="btn-approve" onClick={handleApprove} disabled={approving}>
                      <ApproveIcon /> {approving ? 'Processing...' : 'Approve'}
                    </button>
                    <button className="btn-reject" onClick={() => setRejecting('input')} disabled={approving}>
                      <RejectIcon /> Reject
                    </button>
                  </div>
                )}

                {rejecting === 'input' && (
                  <div className="reject-section">
                    <label className="reject-label">Rejection Reason (Required)</label>
                    <textarea
                      className="reject-input"
                      placeholder="Why are you rejecting this submission?"
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      rows="4"
                    />
                    <div className="reason-actions">
                      <button
                        className="btn-cancel"
                        onClick={() => {
                          setRejecting(false);
                          setRejectReason('');
                        }}
                        disabled={rejecting === true}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn-confirm-reject"
                        onClick={handleReject}
                        disabled={!rejectReason.trim() || rejecting === true}
                      >
                        {rejecting === true ? 'Processing...' : 'Confirm Reject'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
